import moment from "moment";

/* ============================================================================
   AGENDA DE CITAS · reglas de la cuadrícula

   Las cuatro primeras funciones salen TAL CUAL de DoctorScheduleTable.js: se
   extraen para poder probarlas y reutilizarlas, no para cambiarlas. Si alguna
   se comportara distinto a como se comportaba allí, sería un error, no una
   mejora -- las reglas de agendamiento están fuera del alcance del rediseño.
   ========================================================================== */

/** Índice del día de la semana tal y como lo nombran los datos del doctor. */
export const DIAS_SEMANA = [
    "domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado",
];

/** "08:00" + 20 -> "08:20". Cruza la hora correctamente. */
export function sumarMinutos(hora, minutos) {
    const [h, m] = String(hora).split(":").map(Number);
    const totalMinutos = h * 60 + m + minutos;
    const horasFinal = Math.floor(totalMinutos / 60);
    const minutosFinal = totalMinutos % 60;
    return `${String(horasFinal).padStart(2, "0")}:${String(minutosFinal).padStart(2, "0")}`;
}

/**
 * El inverso de `sumarMinutos`: "08:00" y "08:20" -> 20.
 *
 * Hace falta porque una cita GUARDADA trae `hora_inicio` y `hora_fin`, y el
 * modal la muestra como inicio + duracion. No es una regla nueva: es la misma
 * aritmetica de arriba leida al reves.
 *
 * Devuelve `null` cuando falta un dato o cuando el fin no es posterior al
 * inicio. Eso ultimo NO es hipotetico: en la base hay 2 citas con
 * `hora_fin <= hora_inicio`, y el backend no lo valida. Con `null` el modal
 * las muestra tal como estan en vez de inventar una duracion.
 */
export function minutosEntre(horaInicio, horaFin) {
    if (!horaInicio || !horaFin) return null;
    const [hi, mi] = String(horaInicio).split(":").map(Number);
    const [hf, mf] = String(horaFin).split(":").map(Number);
    if ([hi, mi, hf, mf].some((n) => !Number.isFinite(n))) return null;
    const diferencia = (hf * 60 + mf) - (hi * 60 + mi);
    return diferencia > 0 ? diferencia : null;
}

/**
 * Duraciones que la clinica usa DE VERDAD, sacadas de las 2.751 citas activas:
 * 20 min en 2.456 (89 %), y luego 30 (221), 15 (48), 40, 25, 10, 60 y 120.
 * No es un catalogo inventado ni una lista fija: la duracion por defecto sigue
 * saliendo de `clinica_especialidades.intervalo_tiempo`, igual que la
 * cuadricula. Esto solo evita teclear la hora de fin a mano.
 */
export const DURACIONES_CITA = [10, 15, 20, 25, 30, 40, 60, 120];

/**
 * Franjas de la cuadrícula. El intervalo NO es fijo: sale de
 * `clinica_especialidades.intervalo_tiempo`, que en datos reales vale 15, 20,
 * 25, 30 o 40 minutos según la especialidad.
 */
export function calcularIntervalos(horaInicio, horaFin, intervalo) {
    if (!horaInicio || !horaFin || !intervalo) return [];
    const [hi, mi] = String(horaInicio).split(":").map(Number);
    const [hf, mf] = String(horaFin).split(":").map(Number);
    const startTotal = hi * 60 + mi;
    const endTotal = hf * 60 + mf;
    const intervalos = [];
    for (let actual = startTotal; actual < endTotal; actual += intervalo) {
        const horas = Math.floor(actual / 60);
        const minutos = actual % 60;
        intervalos.push(`${String(horas).padStart(2, "0")}:${String(minutos).padStart(2, "0")}`);
    }
    return intervalos;
}

/** ¿El médico trabaja el día de la fecha mostrada? */
export function esDiaLaboral(doctor, fecha) {
    const dia = DIAS_SEMANA[moment(fecha).toDate().getDay()];
    return doctor?.doctors_dias_laborables?.[dia] === true;
}

/** ¿Esa franja cae dentro del horario del médico? (fin excluido) */
export function estaDentroDeHorario(doctor, hora) {
    if (!doctor?.horario_inicio || !doctor?.horario_fin) return false;
    const [hi, mi] = String(doctor.horario_inicio).split(":").map(Number);
    const [hf, mf] = String(doctor.horario_fin).split(":").map(Number);
    const [hh, mh] = String(hora).split(":").map(Number);
    const horaTotal = hh * 60 + mh;
    return horaTotal >= hi * 60 + mi && horaTotal < hf * 60 + mf;
}

/** La cita de ese médico en esa franja, si la hay. */
export function buscarCita(citas, doctor, hora, fecha) {
    const fechaRef = moment(fecha).format("YYYY-MM-DD");
    return (Array.isArray(citas) ? citas : []).find((cita) => {
        const citaHora = String(cita?.hora_inicio).padStart(5, "0");
        const citaFecha = moment(cita?.fecha_inicio).format("YYYY-MM-DD");
        return cita?.iddoctor === doctor?.iddoctor && citaHora === hora && citaFecha === fechaRef;
    });
}

/* ---------------------------------------------------------------------------
   ESTADOS DEL CUPO

   Los 8 estados son los REALES del catálogo `estado_citas`, verificados en la
   base. No se inventa ninguno. `Eliminada` (8) no aparece: el endpoint de la
   agenda la excluye, así que nunca llega.

   El encargo pedía colores para cinco; los otros tres se añadieron a petición.
   Reparto de familias -- seis tokens para siete estados:

     1 Sin Confirmar     ámbar        aún no confirma
     2 Confirmado        azul         el caso normal
     3 En sala de espera celeste      el paciente ya llegó, esperando
     4 Reagendado        ámbar        pendiente, como "sin confirmar", pero con
                                      barra punteada para no confundirlos
     5 En Consulta       verde vivo   siendo atendido ahora
     6 Finalizada        verde + ✓    se atendió, ya terminó
     7 Cancelada         gris tachado no ocurrió

   En Consulta y Finalizada comparten el verde: las dos son atenciones reales.
   Se separan por intensidad (barra viva frente a apagada) y por el check, que
   las hace legibles también para quien no distingue bien tonos. Cancelada es
   la única gris, porque es la única que no ocurrió.
   --------------------------------------------------------------------------- */

export const ESTADOS_CUPO = {
    1: { clave: "sin-confirmar", etiqueta: "Sin confirmar" },
    2: { clave: "confirmada", etiqueta: "Confirmada" },
    3: { clave: "en-sala", etiqueta: "Paciente llegó" },
    4: { clave: "reagendado", etiqueta: "Reagendado" },
    5: { clave: "en-consulta", etiqueta: "En consulta" },
    6: { clave: "finalizada", etiqueta: "Finalizada" },
    7: { clave: "cancelada", etiqueta: "Cancelada" },
};

/** Estados que se muestran en la leyenda, en el orden del flujo real. */
export const LEYENDA_ESTADOS = [1, 2, 3, 5, 6, 7, 4].map((id) => ({
    id,
    ...ESTADOS_CUPO[id],
}));

/**
 * Estado visual de una cita. Si llega un id que no está en el catálogo, se
 * devuelve un estado neutro en vez de romper: un estado desconocido es un dato
 * raro, no un motivo para dejar la agenda en blanco.
 */
export function estadoDeCita(cita) {
    const id = Number(cita?.estado_cita?.idestado_cita);
    return ESTADOS_CUPO[id] ?? { clave: "desconocido", etiqueta: "Sin estado" };
}

/* ---------------------------------------------------------------------------
   PRESENTACIÓN
   --------------------------------------------------------------------------- */

/** "Cesar Damián Robalino" -> "CR". Para el avatar de la cabecera. */
export function iniciales(nombre) {
    const partes = String(nombre ?? "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (partes.length === 0) return "";
    if (partes.length === 1) return partes[0].slice(0, 2).toLocaleUpperCase("es");
    return (partes[0][0] + partes[partes.length - 1][0]).toLocaleUpperCase("es");
}

/**
 * "Intriago Solórzano Ana María" -> "Intriago Solórzano A."
 * En una celda de 46px no cabe un nombre completo: se conservan los apellidos
 * (que es como se busca a un paciente) y el nombre se abrevia.
 */
export function nombreAbreviado(nombreCompleto) {
    const partes = String(nombreCompleto ?? "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (partes.length <= 2) return partes.join(" ");
    const apellidos = partes.slice(0, 2).join(" ");
    return `${apellidos} ${partes[2][0].toLocaleUpperCase("es")}.`;
}

/**
 * Ocupación de un médico en el día: "4/13". El denominador son las franjas
 * DENTRO de su horario, no todas las de la cuadrícula -- decir "4/26" cuando
 * sólo trabaja media jornada sería engañoso.
 */
export function ocupacionDeDoctor(doctor, franjas, citas, fecha) {
    if (!esDiaLaboral(doctor, fecha)) return null;
    const suyas = (franjas ?? []).filter((h) => estaDentroDeHorario(doctor, h));
    const ocupadas = suyas.filter((h) => buscarCita(citas, doctor, h, fecha)).length;
    return { ocupadas, total: suyas.length };
}

/** "08:00 – 18:30" a partir del horario del médico. */
export function rangoHorario(doctor) {
    if (!doctor?.horario_inicio || !doctor?.horario_fin) return "";
    return `${doctor.horario_inicio} – ${doctor.horario_fin}`;
}

/**
 * Posición de la línea de hora actual, en porcentaje sobre la cuadrícula.
 * Devuelve null si la fecha mostrada no es hoy, o si la hora actual cae fuera
 * de la franja pintada: una línea "ahora" en un día que no es hoy sería
 * simplemente falsa.
 */
export function posicionHoraActual(fecha, franjas, intervalo, ahora = moment()) {
    if (!moment(fecha).isSame(ahora, "day")) return null;
    if (!franjas?.length || !intervalo) return null;

    const [hi, mi] = String(franjas[0]).split(":").map(Number);
    const inicioTotal = hi * 60 + mi;
    const finTotal = inicioTotal + franjas.length * intervalo;
    const ahoraTotal = ahora.hours() * 60 + ahora.minutes();

    if (ahoraTotal < inicioTotal || ahoraTotal > finTotal) return null;
    return ((ahoraTotal - inicioTotal) / (finTotal - inicioTotal)) * 100;
}

/* ---------------------------------------------------------------------------
   COLOR DEL MÉDICO

   `doctores.calcolor` es un color que se configura por médico en su ficha, y
   la agenda anterior lo usaba como fondo de la cabecera de su columna. Eso es
   lo que parecía un "alternar azul y negro sin significado": no era un fallo
   de estilos, era el dato -- y 38 de los 51 médicos lo tienen en #000000, que
   es lo que devuelve el selector de color cuando nadie lo ha tocado.

   Aquí se conserva la identidad por color, pero sin repetir los dos problemas
   que tenía:

   · el NEGRO por defecto se trata como "sin configurar" y cae al color del
     sistema. Pintar 38 columnas de negro es exactamente lo que hacía ilegible
     la cabecera;
   · el color no se usa como fondo de toda la cabecera. Hay médicos con
     amarillo (#f0e933) o verde lima (#69ee11): con texto blanco encima no se
     leen. Va en el avatar y en una barra superior de 3px, que identifican la
     columna sin comprometer la lectura del nombre.
   --------------------------------------------------------------------------- */

const HEX_VALIDO = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** El color configurado del médico, o null si no hay uno de verdad. */
export function colorDoctor(doctor) {
    const hex = String(doctor?.calcolor ?? "").trim();
    if (!HEX_VALIDO.test(hex)) return null;
    // #000000 es el valor por defecto del selector, no una elección.
    const normal = hex.length === 4
        ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
        : hex.toLowerCase();
    return normal === "#000000" ? null : normal;
}

/**
 * Texto legible sobre un color: negro o blanco según su luminancia relativa.
 * Sin esto, un avatar amarillo con texto blanco no se lee.
 */
export function contrasteSobre(hex) {
    if (!hex) return null;
    const n = hex.replace("#", "");
    const r = parseInt(n.slice(0, 2), 16) / 255;
    const g = parseInt(n.slice(2, 4), 16) / 255;
    const b = parseInt(n.slice(4, 6), 16) / 255;
    const canal = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
    const luminancia = 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
    return luminancia > 0.45 ? "#1b2330" : "#ffffff";
}
